// components/logistics/JobCard.jsx
import React from 'react';
import { Clock, MapPin, Award, AlertCircle, TrendingUp, Check, X } from 'lucide-react';
import SkillBadge from '../trust/SkillBadge';
import { formatJobForDisplay } from '../../utils/jobMatching';

const JobCard = ({ job, matchResult, onClaim, onViewDetails, isClaimable = true }) => {
  // Guard against undefined job
  if (!job) {
    return null;
  }
  const displayJob = formatJobForDisplay(job, matchResult);
  
  // Determine card styling based on qualification status
  const getCardStyling = () => {
    if (displayJob.qualified) {
      return 'border-green-200 bg-green-50';
    } else if (displayJob.matchScore >= 50) {
      return 'border-yellow-200 bg-yellow-50';
    } else {
      return 'border-gray-200 bg-gray-50';
    }
  };

  const getUrgencyBadge = () => {
    if (displayJob.urgencyLevel === 'critical') {
      return (
        <div className="inline-flex items-center px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium animate-pulse">
          <Clock size={12} className="mr-1" />
          {displayJob.urgencyText}
        </div>
      );
    } else if (displayJob.urgencyLevel === 'urgent') {
      return (
        <div className="inline-flex items-center px-2 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-medium">
          <Clock size={12} className="mr-1" />
          {displayJob.urgencyText}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`rounded-lg border-2 p-4 transition-all hover:shadow-md ${getCardStyling()}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-lg">{job.title}</h3>
            {getUrgencyBadge()}
          </div>
          {job.groupTitle && <p className="text-sm text-gray-600">{job.groupTitle}</p>}
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900">{job.tokenReward}</div>
          <div className="text-xs text-gray-600">tokens</div>
        </div>
      </div>

      <p className="text-sm text-gray-700 mb-3 line-clamp-2">{job.description}</p>

      <div className="space-y-2 mb-3">
        <div className="flex items-center text-sm text-gray-600">
          <Clock size={14} className="mr-2" />
          {displayJob.formattedDeadline && displayJob.formattedDeadline !== 'Flexible' ? (
            <span>{displayJob.formattedDeadline}</span>
          ) : job.schedule?.startTime ? (
            <span>
              {job.schedule.date && new Date(job.schedule.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              {job.schedule.date && ' • '}
              {job.schedule.startTime}{job.schedule.endTime ? ` - ${job.schedule.endTime}` : ''}
            </span>
          ) : (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Flexible Schedule</span>
          )}
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <MapPin size={14} className="mr-2" />
          <span>{job.location?.city ? `${job.location.city}, ${job.location.state}` : (typeof job.location === "string" ? job.location : "Local")}</span>
        </div>
      </div>

      {/* Skills Section */}
      <div className="mb-3">
        <div className="text-xs font-medium text-gray-700 mb-1">Required Skills:</div>
        <div className="flex flex-wrap gap-1">
          {(job.requirements?.requiredSkills || job.requiredSkills || []).map((skill, index) => (
            <div key={skill.id} className="flex items-center">
              <SkillBadge skill={skill} size="small" showStatus={false} />
              {matchResult?.matchingSkills?.some(s => s.id === skill.id) ? (
                <Check size={14} className="ml-1 text-green-600" />
              ) : (
                <X size={14} className="ml-1 text-red-600" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Match Score Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-gray-600 mb-1">
          <span>Match Score</span>
          <span>{displayJob.matchScore}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all ${
              displayJob.matchScore >= 100 ? 'bg-green-500' :
              displayJob.matchScore >= 50 ? 'bg-yellow-500' :
              'bg-gray-400'
            }`}
            style={{ width: `${Math.min(displayJob.matchScore, 100)}%` }}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        {displayJob.qualified && isClaimable ? (
          <button
            onClick={() => onClaim(job)}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Claim Job
          </button>
        ) : displayJob.matchScore >= 50 ? (
          <div className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg text-sm">
            <TrendingUp size={16} />
            <span className="font-medium">
              {displayJob.missingSkills.length} skill{displayJob.missingSkills.length > 1 ? 's' : ''} away
            </span>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm">
            <AlertCircle size={16} />
            <span>Not Qualified</span>
          </div>
        )}
        
        <button
          onClick={() => onViewDetails(job)}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          Details
        </button>
      </div>

      {/* Opportunity Message */}
      {!displayJob.qualified && displayJob.matchScore >= 50 && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-xs text-yellow-800">
            <strong>Get qualified:</strong> Verify {displayJob.missingSkills.map(s => s.name).join(', ')} 
            {!displayJob.meetsTrustRequirement && ` and increase trust score to ${job.minTrustScore}`}
          </p>
        </div>
      )}
    </div>
  );
};

export default JobCard;